import { useQuery } from "@tanstack/react-query";
import Big from "big.js";
import { addDays, format, getUnixTime, startOfDay } from "date-fns-jalali";
import { type Address, formatEther } from "viem";
import { usePublicClient, useReadContract } from "wagmi";
import { ERC20_ABI } from "@/contract/abi/erc20";
import { STAKE_ABI } from "@/contract/abi/stake";
import ContractAddress from "@/contract/addresses";
import { useEffectiveAddress } from "@/hooks/auth/useEffectiveAddress";
import { computeRewardDrip } from "@/lib/stake/reward-drip";
import { safeBigInt } from "@/lib/utils/number-utils";

export type ExtendedUserStake = {
  stake_id: string;
  dao: string;
  uvm: string;
  total_reward: string;
  reward: string | number;
  plan: number;
  exists: boolean;
  duration: bigint;
  land: bigint;
  has_withdraw: boolean;
  start: string;
  // Raw on-chain start timestamp (Unix seconds). Used by UI for date formatting.
  start_sec: bigint;
  _finish: string;
  finish: number;
  can_finish: boolean;
  // NEW Phase 1 fields:
  is_matured: boolean;
  months_past_maturity: number;
  drip_gross_uvm_wei: bigint;
  drip_net_uvm_wei: bigint;
  end_sec: bigint;
  // Remaining lifetime reward in wei = total_reward - total_paid_out (on-chain)
  remaining_reward_uvm_wei: bigint;
};

export type ExtendedStakeData = {
  total_claimable_reward: string;
  total_earning: string;
  user_stake_list: ExtendedUserStake[];
  // NEW Phase 1 fields:
  user_dnm_balance_wei: bigint;
  user_uvm_balance_wei: bigint;
  user_matic_balance_wei: bigint;
  user_staked_nft_count: number;
  user_total_uvm_staked_wei: bigint;
  user_total_dnm_staked_wei: bigint;
};

function isActiveStake(stake: { exists: boolean; finish: bigint }) {
  return stake.exists && stake.finish === 0n;
}

export default function useFetchStakeContractData() {
  const address = useEffectiveAddress();
  const publicClient = usePublicClient();

  // Typed `Address | undefined` — when `enabled: !!address` gates the read,
  // the query only runs with a defined address. No cast required on the args.
  const userAddress: Address | undefined = address;

  const { data: user_stake_list, isLoading: user_stake_list_loading } =
    useReadContract({
      abi: STAKE_ABI,
      functionName: "getUserStake",
      args: userAddress ? [userAddress] : undefined,
      query: { enabled: !!userAddress },
      address: ContractAddress.STAKE,
    });

  const { data: user_stake_list_ids, isLoading: user_stake_list_ids_loading } =
    useReadContract({
      abi: STAKE_ABI,
      functionName: "getUserStakeIds",
      args: userAddress ? [userAddress] : undefined,
      query: { enabled: !!userAddress },
      address: ContractAddress.STAKE,
    });

  async function fetchContractData() {
    if (!address) {
      throw new Error("Connect your wallet");
    }
    // Prereqs guaranteed by `enabled` gate on the useQuery config below:
    // !!address && !user_stake_list_loading && !user_stake_list_ids_loading
    // && !!user_stake_list_ids && !!publicClient. queryFn never fires until
    // all four resolve — no early-return needed.
    if (!publicClient || !user_stake_list_ids) {
      // Should be unreachable — enabled gate guarantees both resolved.
      throw new Error("Stake fetch prereqs missing");
    }

    // Read chain time once and use it as "now" everywhere. Wall-clock
    // `new Date()` diverges from chain `block.timestamp` on a Hardhat fork
    // (forked-block lag, simulated time-travel, or skewed local system
    // clock), and contract `calculateReward(start, end, ...)` reverts with
    // "Invalid start and end" whenever `end < start`. The accrued-now slot
    // passed wall-clock `now` as `end` against on-chain `stake.start` —
    // when chain time was ahead of wall clock, the multicall rejected the
    // entire batch and useQuery never resolved.
    const latestBlock = await publicClient.getBlock();
    const chainNowSec = latestBlock.timestamp;
    const chainNowDate = new Date(Number(chainNowSec) * 1000);

    const calc_today = getUnixTime(startOfDay(chainNowDate));
    const calc_tomorrow = getUnixTime(startOfDay(addDays(chainNowDate, 1)));

    let total_earning = 0;
    let total_uvm_staked_wei = 0n;
    let total_dnm_staked_wei = 0n;

    // SPEED: batch every per-stake calculateReward call (3 calls × N stakes)
    // into a single multicall round-trip. Without this, the page issues up
    // to 3 sequential awaits per stake; under typical RPC latency, a user
    // with 10 stakes pays ~12s just for stake reads. After multicall: one
    // round-trip total. No semantic change to outputs.
    const stakeCalls = user_stake_list?.length
      ? user_stake_list.flatMap((stake_list) => {
          // Slot-0 end uses chain time, not wall clock. Hardhat fork can have
          // chain.timestamp ahead of wall clock (forked-block lag); contract
          // reverts when end < start, killing the entire multicall batch.
          // Clamp to >= stake.start so the fork-edge case (now < start) hits
          // 0n accrued instead of revert.
          const wallOrFinish =
            stake_list.finish > 0n ? stake_list.finish : chainNowSec;
          const finish =
            wallOrFinish < stake_list.start ? stake_list.start : wallOrFinish;
          return [
            // 0: accrued-up-to-now (used for `reward` column + drip)
            {
              abi: STAKE_ABI,
              functionName: "calculateReward" as const,
              args: [
                stake_list.plan,
                stake_list.dnm,
                stake_list.start,
                finish,
                stake_list.stake_duration,
              ] as const,
              address: ContractAddress.STAKE,
            },
            // 1: lifetime at maturity (used as fallback basis in remaining hybrid)
            {
              abi: STAKE_ABI,
              functionName: "calculateReward" as const,
              args: [
                stake_list.plan,
                stake_list.dnm,
                stake_list.start,
                stake_list.start + stake_list.stake_duration,
                stake_list.stake_duration,
              ] as const,
              address: ContractAddress.STAKE,
            },
            // 2: today's earning (daily reward for in-term stakes — see below)
            {
              abi: STAKE_ABI,
              functionName: "calculateReward" as const,
              args: [
                stake_list.plan,
                stake_list.dnm,
                BigInt(calc_today),
                BigInt(calc_tomorrow),
                stake_list.stake_duration,
              ] as const,
              address: ContractAddress.STAKE,
            },
          ];
        })
      : [];

    const stakeResults = stakeCalls.length
      ? await publicClient.multicall({
          contracts: stakeCalls,
          allowFailure: false,
        })
      : [];
    const claimableResults = user_stake_list_ids?.length
      ? await publicClient.multicall({
          contracts: user_stake_list_ids.map((stakeId) => ({
            abi: STAKE_ABI,
            functionName: "calculateRewardForStake" as const,
            args: [stakeId] as const,
            address: ContractAddress.STAKE,
          })),
          allowFailure: true,
        })
      : [];

    const _user_stake_list = user_stake_list?.length
      ? await Promise.all(
          user_stake_list.map(async (stake_list, index) => {
            const accruedRaw = stakeResults[index * 3];
            const lifetimeRewardWei = stakeResults[index * 3 + 1];
            const earningRaw = stakeResults[index * 3 + 2];
            const claimableResult = claimableResults[index];
            const claimableRewardWei =
              claimableResult?.status === "success"
                ? safeBigInt(claimableResult.result)
                : 0n;

            const reward = Big(safeBigInt(accruedRaw).toString());

            // Accumulate staked amounts for active stakes only. `exists` stays
            // true for historical withdrawn records returned by getUserStake.
            if (isActiveStake(stake_list)) {
              total_uvm_staked_wei += stake_list.uvm;
              total_dnm_staked_wei += stake_list.dnm;
            }

            let past_of_stake_duration =
              Number(chainNowSec) -
              (Number(stake_list.start) + Number(stake_list.stake_duration));

            past_of_stake_duration =
              past_of_stake_duration / (30 * 60 * 60 * 24) + 1;

            if (past_of_stake_duration >= 10) {
              past_of_stake_duration = 10;
            }
            const available =
              (+formatEther(safeBigInt(reward)) / 10) * past_of_stake_duration;

            const drip = computeRewardDrip(
              {
                start: stake_list.start,
                stake_duration: stake_list.stake_duration,
                total_reward_wei: safeBigInt(reward),
                total_paid_out_wei: stake_list.total_paid_out,
              },
              chainNowSec,
            );
            const displayDrip = {
              ...drip,
              drip_net_wei: claimableRewardWei,
            };

            // "پاداش روزانه من" (my daily reward) = the stable daily EARNING
            // rate, NOT the throttled withdrawable.
            //
            // calculateReward(today→tomorrow) (slot 2 = earningRaw) already
            // returns base × 0.8^years_passed: FLAT within a DAO-year, stepping
            // down 20% at each year boundary (StakeMeta.calculateReward). We
            // mirror it verbatim — the same number every day for all 10 months,
            // −20% per DAO-year. For #499 on mainnet: 16.876/day (DAO yr 3),
            // having dropped from 21.10 (yr 2) on 2026-05-10.
            //
            // The 10-step monthly release ramp (× min(month,10)/10) is a
            // WITHDRAWAL throttle (StakeMeta.withdrawAllowance), NOT an earning
            // rate. It belongs to the separate "قابل برداشت امروز" box
            // (drip_net_uvm_wei), never here. Folding it in (be4f16f) made the
            // daily number drift with the post-maturity month — it read 15.19 at
            // month 9 then climbed to 16.876 at month 10. The system designer
            // confirmed the daily reward must stay flat regardless of month, so
            // the throttle is removed from this display. The 1% fee is likewise
            // a separate withdraw-time deduction, not folded in.
            if (isActiveStake(stake_list)) {
              total_earning += Number(formatEther(safeBigInt(earningRaw)));
            }

            // total_reward (کل پاداش)         = accrued reward up to NOW (old-front parity, PM 2026-05-10)
            // remaining (مجموع پاداش باقی‌مانده) = effectiveTotal - total_paid_out (hybrid, unchanged)
            //
            // PM 2026-05-10: PM requires exact numeric parity with the legacy
            // old-front for stake card values. Old-front uses
            // `calculateReward(start, finish || now, …)` for total — i.e. accrued
            // up to NOW, which keeps growing past maturity due to drip. The
            // previous lifetime-at-maturity choice (BUG-24 r4) capped the value
            // at the maturity snapshot and diverged from the legacy UI by 5000+
            // UVM on long-matured stakes (e.g. stake 499 showed 9007 vs old
            // 14238). Trade-off accepted: a freshly purchased stake with no
            // elapsed time will show ~0 UVM total reward — same as old-front.
            //
            // remaining stays on the hybrid formula:
            //   effectiveTotalWei = max(lifetimeWei, accruedWei)
            //   remaining = max(0, effectiveTotalWei - total_paid_out)
            //
            // Why hybrid for remaining (unchanged):
            //   - Fresh in-term stakes: accrued ≈ 0 but lifetime > 0; lifetime
            //     path shows "full reward not yet paid". Pure-accrued returned 0.
            //   - Matured drained stakes (BUG-24): accrued > lifetime via drip;
            //     accrued path shows actual unpaid drip. Pure-lifetime returned 0.
            //   - max() picks whichever basis is larger — covers both cases.
            const lifetimeWei = safeBigInt(lifetimeRewardWei);
            const accruedWei = safeBigInt(accruedRaw);
            const effectiveTotalWei =
              lifetimeWei > accruedWei ? lifetimeWei : accruedWei;
            const remaining_reward_uvm_wei =
              effectiveTotalWei > stake_list.total_paid_out
                ? effectiveTotalWei - stake_list.total_paid_out
                : 0n;

            return {
              stake_id: user_stake_list_ids[index].toString(),
              dao: Number(formatEther(stake_list.dnm)).toFixed(2),
              uvm: Number(formatEther(stake_list.uvm)).toFixed(2),
              total_reward: Number(formatEther(accruedWei)).toFixed(2),
              reward:
                past_of_stake_duration > 0
                  ? Number(
                      +formatEther(safeBigInt(reward)) -
                        +formatEther(stake_list.total_paid_out),
                    ).toFixed(2)
                  : 0,
              plan: stake_list.plan,
              exists: stake_list.exists,
              duration: stake_list.stake_duration,
              land: stake_list.land,
              has_withdraw: !!available,
              start: format(
                new Date(Number(stake_list.start) * 1000),
                "yyyy/MM/dd HH:mm:ss",
              ),
              start_sec: stake_list.start,
              _finish: format(
                new Date(Number(stake_list.finish) * 1000),
                "yyyy/MM/dd HH:mm:ss",
              ),
              finish: Number(stake_list.finish),
              can_finish:
                Number(stake_list.stake_duration) <
                Number(chainNowSec) - Number(stake_list.start),
              // Phase 1 extended fields:
              is_matured: displayDrip.is_matured,
              months_past_maturity: displayDrip.months_past_maturity,
              drip_gross_uvm_wei: displayDrip.drip_gross_wei,
              drip_net_uvm_wei: displayDrip.drip_net_wei,
              end_sec: stake_list.start + stake_list.stake_duration,
              remaining_reward_uvm_wei,
            };
          }),
        )
      : [];

    // Aggregate claimable-today = sum of per-stake drip_net_uvm_wei across the full list.
    // Previously this only accumulated post-maturity stakes, so the sum stuck at 0
    // even when cards showed non-zero drip. Use the same per-card metric to stay consistent.
    const total_claimable_reward_wei = _user_stake_list.reduce(
      (acc, s) => acc + s.drip_net_uvm_wei,
      0n,
    );
    const total_claimable_reward = Number(
      formatEther(total_claimable_reward_wei),
    ).toFixed(2);

    // Phase 1: fetch DNM + UVM balances + MATIC native balance
    const [balances, maticBalance] = await Promise.all([
      publicClient.multicall({
        contracts: [
          {
            address: ContractAddress.DNM,
            abi: ERC20_ABI,
            functionName: "balanceOf",
            args: [address],
          },
          {
            address: ContractAddress.UVM,
            abi: ERC20_ABI,
            functionName: "balanceOf",
            args: [address],
          },
        ],
        allowFailure: false,
      }),
      publicClient.getBalance({ address }),
    ]);

    return {
      total_claimable_reward,
      total_earning: Number(total_earning).toFixed(2),
      user_stake_list: _user_stake_list,
      user_dnm_balance_wei: balances[0] as bigint,
      user_uvm_balance_wei: balances[1] as bigint,
      user_matic_balance_wei: maticBalance,
      user_staked_nft_count: _user_stake_list.filter(
        (s) => s.exists && s.finish === 0,
      ).length,
      user_total_uvm_staked_wei: total_uvm_staked_wei,
      user_total_dnm_staked_wei: total_dnm_staked_wei,
    };
  }

  // Derive a stable signature from the wagmi-read inputs so the queryKey
  // changes when on-chain state changes. Without this, invalidating
  // ['readContract'] refreshes wagmi but the aggregate queryFn closure has
  // already captured the stale user_stake_list reference and the cached
  // result is reused. Including a content signature makes useQuery treat
  // post-mutation state as a NEW query and re-runs queryFn with the
  // current closure.
  const stake_signature = user_stake_list_ids
    ? user_stake_list_ids.map((id) => id.toString()).join(",")
    : "";
  const stake_data_signature = user_stake_list
    ? user_stake_list
        .map(
          (s) =>
            `${s.total_paid_out.toString()}-${s.finish.toString()}-${s.exists ? 1 : 0}`,
        )
        .join("|")
    : "";

  return useQuery({
    queryFn: async () => {
      return await fetchContractData();
    },
    queryKey: [
      "stake_contract_data",
      address,
      stake_signature,
      stake_data_signature,
    ],
    enabled:
      !!address &&
      !user_stake_list_loading &&
      !user_stake_list_ids_loading &&
      !!user_stake_list_ids &&
      !!publicClient,
  });
}
